import React, { useState } from 'react';
import {
  IUseUserAssociatedProperties,
  useUserAssociatedProperties,
} from '../../CustomHooks/useUserAssociatedProperties';
import {
  IUseKeyPickupSummaries,
  useKeyPickupSummaries,
} from '../../CustomHooks/useKeyPickupSummaries/useKeyPickupSummaries';
import {
  IUseMyPropertyBookingLinks,
  useMyPropertyBookingLinks,
} from '../../CustomHooks/useMyPropertyBookingLinks/useMyPropertyBookingLinks';
import { IUserAssociatedProperty, JuniperEventType } from '../../types';
import KeyPickupPropertyCard from '../KeyPickupPropertyCard/KeyPickupPropertyCard';
import KeyPickupInviteIntroCallout from '../KeyPickupInviteIntroCallout/KeyPickupInviteIntroCallout';
import KeyPickupInviteDialog from '../KeyPickupInviteDialog/KeyPickupInviteDialog';
import KeyPickupsManagerSkeleton from './KeyPickupsManagerSkeleton';
import './key-pickups-manager.scss';

const KeyPickupsManager = () => {
  const {
    properties,
    isLoading: isLoadingProperties,
    error: propertiesError,
  }: IUseUserAssociatedProperties = useUserAssociatedProperties();

  const {
    summaryFor,
    isLoading: isLoadingSummaries,
    error: summariesError,
  }: IUseKeyPickupSummaries = useKeyPickupSummaries();

  const {
    linksFor,
    isLoading: isLoadingLinks,
    error: linksError,
  }: IUseMyPropertyBookingLinks = useMyPropertyBookingLinks();

  const [manageProperty, setManageProperty] =
    useState<IUserAssociatedProperty | null>(null);

  const isLoading: boolean =
    isLoadingProperties || isLoadingSummaries || isLoadingLinks;

  const error: string | null =
    propertiesError ?? summariesError ?? linksError;

  if (isLoading) {
    return <KeyPickupsManagerSkeleton />;
  }

  if (error) {
    return <p className="error-text">{error}</p>;
  }

  if (properties.length === 0) {
    return (
      <p className="key-pickups-muted">
        You don&apos;t manage any properties yet.
      </p>
    );
  }

  return (
    <div className="key-pickups-manager">
      <KeyPickupInviteIntroCallout />

      <div className="property-card-grid">
        {properties.map((property: IUserAssociatedProperty) => (
          <KeyPickupPropertyCard
            key={property.propertyId}
            property={property}
            summary={summaryFor(property.propertyId)}
            hasKeyPickupLink={
              linksFor(property.propertyId, JuniperEventType.KeyPickup).length > 0
            }
            onManage={setManageProperty}
          />
        ))}
      </div>

      {manageProperty && (
        <KeyPickupInviteDialog
          key={manageProperty.propertyId}
          property={manageProperty}
          onClose={() => setManageProperty(null)}
        />
      )}
    </div>
  );
};

export default KeyPickupsManager;